<template>
  <div
    v-if="description"
    class="descriptionEditor"
  >
    <b-row class="header-banner d-flex align-items-center mb-2 mt-1">
      <b-col class="h-auto">
        <span class="ap-title ap-title__main">{{ descriptionTitle }}</span>
      </b-col>
      <b-col
        cols="auto"
        class="d-flex justify-content-end"
      >
        <div>
          <template v-if="!hideEditOperations">
            <b-button
              v-tooltip="$t('global.description.clear')"
              qa="description-editor-clear-button"
              class="btn__icon btn__icon--primary  px-2"
              :disabled="disabled"
              @click="cleanDescriptionText"
            >
              <i
                class="far fa-eraser"
                aria-hidden="true"
              />
            </b-button>
            <b-button
              v-if="isDescriptionTextNotEditable"
              v-tooltip="$t('global.edit')"
              qa="description-editor-update-button"
              class="btn__icon btn__icon--primary  px-2"
              :disabled="disabled"
              @click="openTemplatesModalIfDescriptionTextNotEditable"
            >
              <i
                class="far fa-edit"
                aria-hidden="true"
              />
            </b-button>
            <b-button
              v-tooltip="$t('global.description.textTemplate')"
              v-shortkey="['alt', 't']"
              qa="description-editor-text-template-button"
              class="btn__icon btn__icon--primary  px-2"
              :disabled="disabled"
              @shortkey.prevent="showTemplatesModal(false)"
              @click="showTemplatesModal(false)"
            >
              <i
                class="far fa-file-alt"
                aria-hidden="true"
              />
            </b-button>
            <b-button
              v-tooltip="$t('global.description.synopticTemplate')"
              qa="description-editor-synoptic-template-button"
              class="btn__icon btn__icon--primary  px-2"
              :disabled="disabled"
              @click="showSynopticTemplatesModal(false)"
            >
              <i
                class="far fa-file-edit"
                aria-hidden="true"
              />
            </b-button>
          </template>
          <b-button
            v-if="entityOfDescriptionAllowsAttachments"
            v-tooltip="$t('global.description.audioControls')"
            qa="description-editor-audio-sidepanel-button"
            class="btn__icon btn__icon--primary  px-2"
            :class="{'btn-active':audioSidepanel}"
            style="width:30px;position:relative"
            @click="showAudioSidepanel()"
          >
            <i
              class="far fa-volume-up"
              aria-hidden="true"
            />
            <div
              v-if="descriptionAudioAttachments.length > 0"
              class="btn-badge"
            >
              <span class="badge vt-nav-badge indicator-bubble badge-secondary">
                {{ descriptionAudioAttachments.length }}
              </span>
            </div>
          </b-button>
          <b-button
            v-if="entityOfDescriptionAllowsAttachments"
            v-tooltip="$t('global.description.associatePictures')"
            qa="description-editor-associated-images-button"
            class="btn__icon btn__icon--primary  px-2"
            :class="{'btn-active':pictureSidepanel}"
            style="width:30px;position:relative"
            @click="showPictureSidepanel()"
          >
            <i
              class="far fa-images"
              aria-hidden="true"
            />
            <div
              v-if="associatedImages && associatedImages.length"
              class="btn-badge"
            >
              <span class="badge vt-nav-badge indicator-bubble badge-secondary">
                {{ associatedImages.length }}
              </span>
            </div>
          </b-button>
        </div>
      </b-col>
    </b-row>

    <b-row class="diagnosis-layout-height">
      <b-col
        :class="{'d-none d-lg-block': pictureSidepanel || audioSidepanel}"
        @dblclick="openTemplatesModalIfDescriptionTextNotEditable"
      >
        <vt-rich-text-editor
          ref="richTextEditor"
          :key="refreshEditor"
          qa="description-editor-textarea"
          class="mt-1"
          autofocus
          :disabled="disabled || hideEditOperations"
          input-id="descriptionDescriptionInput"
          name="richTextEditor"
          :value="descriptionText"
          :allow-images="false"
          @input="descriptionText = $event"
        />
      </b-col>
      <!-- Audio section inside Description box-->
      <b-col
        v-if="audioSidepanel"
        xl="3"
        lg="5"
        class="static-audio-col mt-md-3"
      >
        <span v-if="!disabled && !hideAttachOperations">
          <b-row class="h-auto">
            <b-col>
              <embedded-voice-recorder
                :max-minutes-length="maxMinutesLengthForDescriptionAudio"
                :filename="nextFilenameForAudioDescription"
                :callback-to-save-record="saveDescriptionAudio"
              />
            </b-col>
          </b-row>
          <div class="horizontal-divider my-2" />
        </span>
        <b-row class="audio-scroll">
          <b-col>
            <b-col
              v-if="!descriptionAudioAttachments.length"
              class="text-center p-3"
            >
              <span><em>{{ $t('global.description.noRecordingsAvailable') }}</em></span>
            </b-col>
            <div
              v-for="attachment in descriptionAudioAttachments"
              :key="attachment.attachmentId"
              class="audio-box px-2 p-1 mb-2"
              :class="{'audio-selected': selectedAudio === attachment}"
            >
              <b-row>
                <b-col
                  class="audio-click"
                  @click="selectedAudio = attachment"
                  @dblclick="playSelectedAudio(attachment)"
                >
                  <span>{{ removeExtensionFromFilename(attachment.filename) }}</span>
                </b-col>
                <b-col cols="auto">
                  <div class="d-flex justify-content-center align-items-center">
                    <b-button
                      :id="'attachment-audio-'+attachment.attachmentId"
                      class="btn__icon btn__icon--primary px-2"
                    >
                      <i
                        class="far fa-user"
                        aria-hidden="true"
                      />
                    </b-button>
                    <b-popover
                      :target="'attachment-audio-'+attachment.attachmentId"
                      placement="bottom"
                      triggers="click blur"
                    >
                      <ul class="simple-pop-inside-reg">
                        <li class="py-1">
                          {{ $t('global.description.author') }}:
                          {{ attachment.createdBy.fullName }}
                        </li>
                        <li class="py-1">
                          <i
                            class="far fa-calendar-alt main mr-2"
                            aria-hidden="true"
                          />
                          {{ attachment.creationDate | datetime }}
                        </li>
                      </ul>
                    </b-popover>
                    <a
                      :href="generateUrlForAttachment(attachment.attachmentId)"
                      :download="removeExtensionFromFilename(attachment.filename)"
                      target="_blank"
                    >
                      <b-button class="btn__icon btn__icon--primary px-2">
                        <i
                          class="far fa-download"
                          aria-hidden="true"
                        />
                      </b-button>
                    </a>
                    <b-button
                      v-if="!hideAttachOperations"
                      class="btn__icon btn__icon--primary-error btn__icon btn__icon--primary px-2"
                      :disabled="disableAttachOperations"
                      @click="deleteDescriptionAudioAttachment(attachment.attachmentId)"
                    >
                      <i
                        class="far fa-times"
                        aria-hidden="true"
                      />
                    </b-button>
                  </div>
                </b-col>
              </b-row>
            </div>
          </b-col>
        </b-row>
        <div
          v-if="descriptionAudioAttachments.length"
          class="horizontal-divider my-2"
        />
        <b-row
          v-if="descriptionAudioAttachments.length"
          class="mt-3"
        >
          <b-col>
            <audio-player
              ref="audioPlayer"
              selected-audio-recording-url
              :src="selectedAudioRecordingUrl"
              :record="{}"
            />
          </b-col>
        </b-row>
      </b-col>
      <b-col
        v-if="pictureSidepanel"
        xl="3"
        lg="5"
      >
        <associated-images-side-panel
          :disabled="disabled"
          :hide-attach-operations="hideEditOperations"
          :associated-images="associatedImages"
          :complementary-id="complementaryId"
          :diagnosis-section="diagnosisSection"
          @deleteAttachment="deleteAttachment($event)"
          @showAssociatedImagesModal="showAssociatedImagesModal"
        />
      </b-col>
    </b-row>

    <!--Modals-->
    <text-templates-modal
      v-if="description && !disabled"
      ref="templatesModal"
      :laboratory-id="laboratoryIdLogged"
      :description-laboratory-id="description.templateLaboratoryId"
      :case-id="caseId"
      :sample-id="sampleId"
      :template-id-in-use="description.templateId"
      :options-selected-for-template-in-use="description.optionsSelected"
      :ui-component-code="textTemplateModalUiComponentCode"
      @hidden="onTextTemplatesModalHidden()"
      @templateSelected="applyTemplateChange"
    />
    <modal-confirmation
      id="onReplacingDescriptionText"
      ref="onReplacingDescriptionText"
      :content="$t('global.description.modal.onReplacingDescriptionText')"
    />
    <modal-confirmation-checkable
      id="onReplacingDescriptionTextCheckable"
      ref="onReplacingDescriptionTextCheckable"
      :content="$t('global.description.modal.onReplacingDescriptionText')"
      :store-name="modalConfirmationStateStore"
      :ui-component="modalConfirmationStateUiComponentCode"
      :name="modalConfirmationStateName"
    />
    <modal-description-concurrence
      v-if="concurrenceError"
      ref="descriptionConcurrenceModal"
      :laboratory-id="laboratoryId"
      :description-service="descriptionService"
      :parent-entity-ids-array="parentEntityIdsArray"
      :root-filename-for-audio-description="rootFilenameForAudioDescription"
      :attachment-type-id-for-description-audio="attachmentTypeIdForDescriptionAudio"
      :attachment-service-for-audio-attachments="attachmentServiceForAudioAttachments"
      :case-id="caseId"
      :sample-id="sampleId"
      :attachment-service="attachmentService"
      @hide="concurrenceError = false"
    />
    <synoptic-templates-modal
      ref="synopticTemplatesModal"
      :laboratory-id="laboratoryIdLogged"
      :description-laboratory-id="description.templateLaboratoryId"
      :case-id="caseId"
      :sample-id="sampleId"
      :template-id-in-use="description.templateId"
      :options-selected-for-template-in-use="description.optionsSelected"
      :ui-component-code="textTemplateModalUiComponentCode"
      @templateSelected="applyTemplateChange"
      @openAssociatedImagesModal="openAssociatedImagesModalSingleSelection($event)"
    />
    <modal-wrapper ref="associatedImagesModal">
      <associated-images-modal
        :associated-images="associatedImages"
        :laboratory-id="laboratoryIdLogged"
        :case-id="caseId"
        :complementary-id="complementaryId"
        :diagnosis-section="diagnosisSection"
        @associateImages="associateImages($event)"
        @attachmentNameUpdated="getDescriptionAttachments"
        @attachments-updated="onAttachmentsUpdated"
      />
    </modal-wrapper>
  </div>
</template>

<script>
import TextTemplatesModal from '@/components/description/TextTemplatesModal'
import EmbeddedVoiceRecorder from '@/components/attachments/EmbeddedVoiceRecorder'
import { DescriptionType } from '@/api/resources/description/description.model'
import { cloneDeep, isEqual } from 'lodash'
import { AudioPlayer } from 'vue-audio-recorder'
import ModalConfirmation from '@/components/modals/ModalConfirmation'
import parameterService from '@/api/resources/parameterService'
import ModalDescriptionConcurrence from '@/components/modals/ModalDescriptionConcurrence'
import SynopticTemplatesModal from '@/modules/diagnosisModule/views/form/modals/SynopticTemplatesModal'
import apiExceptionType from '@/utils/apiExceptionType'
import ModalConfirmationCheckable from '@/components/modals/ModalConfirmationCheckable'
import AssociatedImagesModal from '@/components/attachments/AssociatedImagesModal'
import AssociatedImagesSidePanel from '@/components/attachments/AssociatedImagesSidePanel'

export default {
    name: 'DescriptionEditor',
    components: {
        AssociatedImagesSidePanel,
        AssociatedImagesModal,
        TextTemplatesModal,
        EmbeddedVoiceRecorder,
        AudioPlayer,
        ModalConfirmation,
        ModalDescriptionConcurrence,
        SynopticTemplatesModal,
        ModalConfirmationCheckable
    },
    props: {
        laboratoryId: { type: Number, required: true },
        descriptionTitle: { type: String, required: true },
        descriptionService: { type: Object, required: true },
        parentEntityIdsArray: { type: Array, required: true },
        rootFilenameForAudioDescription: { type: String, required: true },
        attachmentTypeIdForDescriptionAudio: { type: Number, required: true },
        attachmentServiceForAudioAttachments: { type: Object, required: true },
        textTemplateModalUiComponentCode: { type: String },
        modalConfirmationStateUiComponentCode: { type: String },
        modalConfirmationStateStore: { type: String },
        modalConfirmationStateName: { type: String },
        disabled: { type: Boolean, default: false },
        hideEditOperations: { type: Boolean, default: false },
        hideAttachOperations: { type: Boolean, default: false },
        disableAttachOperations: { type: Boolean, default: false },
        caseId: { type: Number },
        sampleId: { type: Number },
        attachmentService: { type: Object },
        entityOfDescriptionAllowsAttachments: { type: Boolean, default: true },
        diagnosisSection: { type: String },
        complementaryId: { type: Number }
    },
    data() {
        return {
            associatedImages: null,
            selectedAudio: null,
            audioSidepanel: false,
            pictureSidepanel: false,
            description: null,
            originalDescription: null,
            descriptionAudioAttachments: [],
            maxMinutesLengthForDescriptionAudio: 5,
            descriptionText: '',
            refreshEditor: 0,
            concurrenceError: false,
            singleSelection: false,
            synopticTemplateAssociatedImages: []
        }
    },
    computed: {
        laboratoryIdLogged() {
            return this.$store.getters.labId
        },
        selectedAudioRecordingUrl() {
            if (this.selectedAudio) {
                return this.generateUrlForAttachment(this.selectedAudio.attachmentId)
            } else {
                return ''
            }
        },
        nextFilenameForAudioDescription() {
            return this.rootFilenameForAudioDescription + String(this.descriptionAudioAttachments.length + 1).padStart(2, '0')
        },
        isDescriptionTextNotEditable() {
            return this.description?.descriptionType === DescriptionType.TEXT_TEMPLATE ||
                this.description?.descriptionType === DescriptionType.SYNOPTIC_TEMPLATE
        }
    },
    watch: {
        descriptionText(value) {
            this.description.text = value
        },
        disabled() {
            this.refreshEditor++
        }
    },
    async created() {
        await this.loadData()
    },
    async mounted() {
        await this.getDescriptionAttachments()
    },
    methods: {
        onAttachmentsUpdated() {
            this.$emit('attachments-updated')
        },
        onTextTemplatesModalHidden() {
            this.$refs.richTextEditor.focus()
        },
        openAssociatedImagesModalSingleSelection(nodeCode) {
            this.$refs.associatedImagesModal.show([null, nodeCode])
        },
        async deleteAttachment(attachment) {
            this.updateLoading(true)
            const [err] = await this.toCall(this.attachmentService.deleteAttachment(...this.parentEntityIdsArray, attachment.id))
            this.updateLoading(false)
            if (err) {
                this.showErrorModalGlobal()
            }
            this.showToastSuccessGlobal()
            await this.getDescriptionAttachments()
        },
        async showAssociatedImagesModal() {
            await this.getDescriptionAttachments()
            this.$refs.associatedImagesModal.show([null, null, this.sampleId])
        },
        async associateImages(attachmentsToAdd) {
            if (attachmentsToAdd.nodeCode) {
                this.$refs.synopticTemplatesModal.setAssociatedImages(attachmentsToAdd)
            } else {
                await this.addAttachmentToDescription(attachmentsToAdd)
                if (this.pictureSidepanel) {
                    await this.getDescriptionAttachments()
                }
            }
        },
        showAudioSidepanel() {
            this.audioSidepanel = !this.audioSidepanel
            if (this.audioSidepanel) {
                this.pictureSidepanel = false
            }
        },
        async addAttachmentToDescription(attachmentsToAdd) {
            for (const attachmentComponent of attachmentsToAdd.attachments) {
                const request = {
                    attachmentEntityType: attachmentsToAdd.attachmentEntityType,
                    attachmentId: attachmentComponent.attachment.attachmentId
                }
                this.updateLoading(true)
                const [err] = await this.toCall(this.attachmentService.addAttachment(...this.parentEntityIdsArray, request))
                this.updateLoading(false)
                if (err) {
                    this.showErrorModalGlobal()
                }
            }
        },
        async showPictureSidepanel() {
            this.pictureSidepanel = !this.pictureSidepanel
            if (this.pictureSidepanel) {
                await this.getDescriptionAttachments()
                this.audioSidepanel = false
            }
        },
        async getDescriptionAttachments() {
            this.updateLoading(true)
            const [err, response] = await this.toCall(this.attachmentService.getAttachments(...this.parentEntityIdsArray))
            this.updateLoading(false)
            if (err) {
                this.showErrorModalGlobal()
                return
            }
            this.associatedImages = response
        },
        showTemplatesModal(loadCurrentTemplateDetails) {
            this.$refs.templatesModal.show(loadCurrentTemplateDetails)
        },
        showSynopticTemplatesModal(loadCurrentTemplateDetails) {
            this.$refs.synopticTemplatesModal.show(loadCurrentTemplateDetails)
        },
        async loadData() {
            this.updateLoading(true)
            await Promise.all([
                this.loadDescription(),
                this.loadDescriptionAudioAttachments(),
                this.loadParameters()
            ])
            this.updateLoading(false)
        },
        async loadDescription() {
            const [error, response] = await this.toCall(this.descriptionService.findById(...this.parentEntityIdsArray))
            if (error) {
                this.showErrorModalGlobal(this.$t('global.description.error.loadingDescription'))
                return
            }
            this.description = response
            this.originalDescription = cloneDeep(this.description)
            await this.updateDescriptionText()
        },
        async setDescriptionManually(description) {
            this.description = cloneDeep(description)
            await this.updateDescriptionText()
        },
        async loadParameters() {
            const [error, response] = await this.toCall(
                parameterService.findMaxMinutesLengthForDescriptionAudioRecordings(this.laboratoryId))
            if (error) {
                this.showErrorModalGlobal(this.$t('global.error.loadingParameters'))
                return
            }
            this.maxMinutesLengthForDescriptionAudio = response
        },
        async loadDescriptionAudioAttachments() {
            const [error, response] = await this.toCall(
                this.attachmentServiceForAudioAttachments.getAttachmentsForAttachmentType(...this.parentEntityIdsArray, this.attachmentTypeIdForDescriptionAudio)
            )
            if (error) {
                this.showErrorModalGlobal(this.$t('global.description.error.loadingDescriptionAudioAttachments'))
                return
            }
            this.descriptionAudioAttachments = response
            if (this.descriptionAudioAttachments.length > 0) {
                this.descriptionAudioAttachments = this.descriptionAudioAttachments.sort((a, b) => {
                    if (a.filename < b.filename) {
                        return -1
                    } else if (b.filename < a.filename) {
                        return 1
                    }
                    return 0
                })
                this.selectedAudio = this.descriptionAudioAttachments[0]
            }
        },
        hasBeenChanged() {
            return !isEqual(this.originalDescription, this.description)
        },
        async save(showSuccessToast = true) {
            if (!this.hasBeenChanged()) {
                return true
            }
            this.updateLoading(true)
            const [error] = await this.toCall(this.descriptionService.updateDescription(...this.parentEntityIdsArray, this.description))
            this.updateLoading(false)
            if (error) {
                if (error.response.data.code === this.$apiExceptions.CONCURRENCY_ERROR) {
                    this.concurrenceError = true
                    this.$nextTick(() => this.$refs.descriptionConcurrenceModal.openModalToSolveConflict(
                        error.response.data.currentDescription,
                        this.description,
                        error.response.data.currentConcurrencyUpdatedBy,
                        () => {
                            this.loadData()
                            this.concurrenceError = false
                        }
                    ))
                } else if (error.response.data.code === this.$apiExceptions.ADD_OR_REPLACE_CODIFICATIONS_WHEN_APPLYING_SYNOPTIC_TEMPLATE_EXCEPTION) {
                    const confirmModalResult = await this.showConfirmationModalGlobal(this.$t('global.codificationsHaveChangedAfterApplyingSynopticTemplate'), this.$t('diagnosis.preformatted.add'), this.$t('diagnosis.preformatted.replace'))
                    const replaceInsteadOfAdd = !confirmModalResult
                    this.updateLoading(true)
                    const [updateDescriptionAndSolveCodificationsErr] = await this.toCall(this.descriptionService.updateDescriptionAndSolveCodificationsChange(...this.parentEntityIdsArray, this.description, replaceInsteadOfAdd))
                    this.updateLoading(false)
                    if (updateDescriptionAndSolveCodificationsErr) {
                        this.showErrorModalGlobal()
                        return false
                    } else {
                        await this.loadData()
                        this.showToastSuccessGlobal()
                        return true
                    }
                } else {
                    this.showErrorModalGlobal(error.response.data.message)
                }
                return false
            }
            await this.loadData()
            if (showSuccessToast) {
                this.showToastSuccessGlobal()
            }
            return true
        },
        async deleteDescriptionAudioAttachment(attachmentId) {
            const [error] = await this.toCall(
                this.attachmentServiceForAudioAttachments.deleteAttachment(...this.parentEntityIdsArray, attachmentId)
            )
            if (error) {
                this.showErrorModalGlobal(this.$t('global.description.error.deletingDescriptionAudioAttachment'))
                return
            }
            this.selectedAudio = null
            await this.loadDescriptionAudioAttachmentsAndUpdate()
        },
        async loadDescriptionAudioAttachmentsAndUpdate() {
            this.$emit('attachments-updated')
            await this.loadDescriptionAudioAttachments()
        },
        generateUrlForAttachment(attachmentId) {
            return this.attachmentServiceForAudioAttachments.getUrlForAttachment(...this.parentEntityIdsArray, attachmentId)
        },
        async saveDescriptionAudio(file) {
            this.updateLoading(true)
            const [err] = await this.toCall(this.attachmentServiceForAudioAttachments.addAttachment(...this.parentEntityIdsArray, this.attachmentTypeIdForDescriptionAudio, file))
            this.updateLoading(false)
            if (err) {
                if (err.response.data.code === apiExceptionType.MAX_UPLOAD_SIZE_EXCEEDED || err.response.status === apiExceptionType.ENTITY_TOO_LARGE) {
                    this.showErrorModalGlobal(this.$t('component.attachModal.error.maxFileSizeExceeded'))
                } else {
                    this.showErrorModalGlobal(this.$t('global.description.error.uploadingAudio'))
                }
            } else {
                await this.loadDescriptionAudioAttachmentsAndUpdate()
                this.showToastSuccessGlobal()
            }
        },
        playSelectedAudio(audio) {
            this.selectedAudio = audio
            this.$nextTick(() => {
                this.$refs.audioPlayer.playback()
            })
        },
        async applyTemplateChange(descriptionInfo) {
            if (!this.description) {
                this.description = {}
            }

            if (!this.description.text) {
                await this.replaceDescriptionDescription(descriptionInfo)
            } else if (
                !descriptionInfo.textEditingAllowed ||
                this.description.descriptionType !== DescriptionType.FREE_TEXT
            ) {
                if (descriptionInfo.templateId === this.description.templateId) {
                    await this.replaceDescriptionDescription(descriptionInfo)
                } else if (this.modalConfirmationStateUiComponentCode) {
                    if (!this.getModalConfirmationState()) {
                        await this.$refs.onReplacingDescriptionTextCheckable.show(() => this.replaceDescriptionDescription(descriptionInfo))
                    } else {
                        await this.replaceDescriptionDescription(descriptionInfo)
                    }
                } else {
                    await this.$refs.onReplacingDescriptionText.ask(() => this.replaceDescriptionDescription(descriptionInfo))
                }
            } else if (descriptionInfo.textEditingAllowed) {
                await this.applyChangesFromDescriptionInfoToDescription(descriptionInfo)
            }
        },
        getModalConfirmationState() {
            return this.$_getModalConfirmationState(this.modalConfirmationStateStore, this.modalConfirmationStateName)
        },
        async replaceDescriptionDescription(descriptionInfo) {
            this.description.text = ''
            await this.applyChangesFromDescriptionInfoToDescription(descriptionInfo)
        },
        async applyChangesFromDescriptionInfoToDescription(descriptionInfo) {
            if (this.description.text.length > 0) {
                this.description.text += '<br/>'
            }
            this.description.text += descriptionInfo.text

            if (descriptionInfo.textEditingAllowed) {
                this.description.descriptionType = DescriptionType.FREE_TEXT
            } else if (descriptionInfo.descriptionType === DescriptionType.SYNOPTIC_TEMPLATE) {
                this.description.descriptionType = DescriptionType.SYNOPTIC_TEMPLATE
            } else {
                this.description.descriptionType = DescriptionType.TEXT_TEMPLATE
            }

            this.description.templateId = descriptionInfo.templateId
            this.description.optionsSelected = descriptionInfo.optionsSelected
            await this.updateDescriptionText()
        },
        async updateDescriptionText() {
            this.descriptionText = this.description.text
            this.refreshEditor++

            if (this.description.descriptionType !== DescriptionType.FREE_TEXT) {
                this.$nextTick(() => this.$refs.richTextEditor.makeNonEditable())
            }

            await this.getDescriptionAttachments()
        },
        openTemplatesModalIfDescriptionTextNotEditable() {
            if (!this.disabled && this.isDescriptionTextNotEditable) {
                if (this.description.descriptionType === DescriptionType.TEXT_TEMPLATE) {
                    this.$refs.templatesModal.show()
                } else if (this.description.descriptionType === DescriptionType.SYNOPTIC_TEMPLATE) {
                    this.$refs.synopticTemplatesModal.show()
                }
            }
        },
        cleanDescriptionText() {
            this.descriptionText = ''
            this.description.text = ''
            this.description.descriptionType = DescriptionType.FREE_TEXT
            this.description.templateId = null
            this.description.optionsSelected = null
            this.$refs.templatesModal.cleanSelected()
            this.$refs.synopticTemplatesModal.cleanSelected()
            this.refreshEditor++
        },
        showSynopticModal() {
            this.$refs.synopticModal.show()
        }
    }
}
</script>

<style lang="scss">
.pictures-button {
    display: none;
}
.case-inform .pictures-button {
    display: inline-block;
}
.case-inform .descriptionEditor .btn-badge {
    left: 0.85rem;
    top: 0.5rem;
    z-index: 1;
}
.grossingSample .descriptionEditor .btn-badge {
    left: 17px !important;
    top: 10px !important;
}
.descriptionEditor .empty-pictures p {
    font-size: font-size(small);
}

.descriptionEditor .btn-active {
        background-color: vt-color(primary) !important;
        color: vt-color(white-light) !important;
}

.descriptionEditor .picture-card {
    margin-top: 10px;
}

.descriptionEditor .picture-card .card-body {
    padding: 0;
}

.descriptionEditor .picture-card .picture-zone img {
    width: 100%;
    height: 100%;
    object-fit: contain;
}

.descriptionEditor .picture-card .picture-zone {
    height: 120px;
    background: vt-color(black, .9);
    border-radius: 0.25em 0.25em 0 0;
}

.descriptionEditor .picture-card .float-btn {
    position: absolute;
    top: 0;
    right: 0;
    color: vt-color(white-light);
    border-radius: 50%;
    width: 23px;
    height: 23px;
    display: flex;
    align-items: center;
    justify-content: center;
    margin: 5px;
    opacity: .7;
}

.descriptionEditor .picture-card .picture-text {
    font-size: font-size(small);
}

.descriptionEditor .picture-scroll {
    height: calc(100vh - 380px);
    min-height: 280px;
    overflow-y: auto;
}

.section-complementaries .picture-scroll {
    height: calc(100vh - 510px) !important;
}

.descriptionEditor .ql-snow .ql-editor img {
    max-width: 200px;
}
.descriptionEditor .ql-editor p {
    word-break: break-word;
}
.descriptionEditor .ql-toolbar.ql-snow {
  padding: 2px !important;
  background: vt-color(rich-text-editor-tools-bg, .7);
}

.descriptionEditor .ql-editor {
  font-size: 1rem;
}

.descriptionEditor .ql-tooltip {
    z-index: 1;
}

.descriptionEditor .static-audio-col .ar-icon {
  width: 30px;
  height: 30px;
  line-height: 0;
  border-color: vt-color(primary);
}

.descriptionEditor .static-audio-col .ar-player__play--active {
  background-color: vt-color(primary);
}

.descriptionEditor .static-audio-col .ar-player__time {
  font-size: 12px;
}

.descriptionEditor .static-audio-col .ar-player > .ar-player-bar > .ar-player__progress {
  width: 40%;
}

.descriptionEditor .static-audio-col .ar-player {
  width: 100%;
}

.descriptionEditor .static-audio-col .ar-volume__icon {
  width: 15px;
  height: 15px;
}

.descriptionEditor .static-audio-col .ar-volume-bar {
  width: 100%;
}

.descriptionEditor .static-audio-col .ar-volume {
  width: 30%;
}

.descriptionEditor .static-audio-col .ar-player-bar {
  width: 85%;
}

.descriptionEditor .static-audio-col .ar-player-actions {
  width: 15%;
}

.descriptionEditor .col-audio {
  flex: 0 0 61.66667% !important;
}

@media (max-width: 1370px) {
  .descriptionEditor .static-audio-col .ar-player > .ar-player-bar > .ar-player__progress {
    width: 40%;
  }

  .descriptionEditor .static-audio-col .ar-volume-bar {
    width: 30px;
  }

  .case-inform .descriptionEditor .diagnosis-layout-height {
      height: calc(100vh - 325px);
  }

  .case-inform .section-complementaries .descriptionEditor .diagnosis-layout-height {
       height: calc(100vh - 452px);
   }
}

.descriptionEditor .radio-btn .btn {
  cursor: pointer;
}
</style>

<style lang="scss" scoped>
.btn__icon {
    width: 30px;
}
.static-audio-col {
    /*TODO: No to delete next line until confirmation*/
    /*flex: 0 0 280px;*/
}

.audio-box {
  width: 100%;
  transition: 0.2s;
    @include custom-card();
}

.audio-selected {
  border-color: vt-color(primary);
  color: vt-color(primary);
}

.audio-box .audio-click {
  cursor: pointer;
  transition: 0.3s;
}

.audio-box .audio-click:hover {
  color: vt-color(primary);
}

.audio-box .audio-click .state {
  color: vt-color(base-text);
  font-size: 10px;
}

.audio-scroll {
  height: calc(100vh - 460px);
  min-height: 220px;
  overflow-y: auto;
}
</style>
